import { Container } from 'react-bootstrap';
import { SpotLoading } from '@enterprise/spot';
import { observer } from 'mobx-react';
import { CombinedItemSearchPanel } from './itemsSearchPanel';
import { CombinedItemsGrid, CombinedItemsGridColumn } from '../combinedItemsGrid';
import { ItemsGroupByType } from '../../../../core/models/organization/settings/enum';
import { useStores } from '../../../../hooks';
import styles from './combinedItemsView.component.module.scss';
import { CombinedItem, GetCombinedItemQuery } from '../../../../core/models';
import { ItemAddPractices } from '../../components/ItemAddPractice/ItemAddPractices';
import i18n from 'i18next';
import { useCombinedItemBulkAction } from './useCombinedItemBulkAction.hook';
import { CombinedItemBulkUpdateType } from '../../../../store';

export const CombinedItemsView = observer(function CombinedItemsViewComponent() {
    const {
        domain: { constantsStore, combinedItemStore },
    } = useStores();

    const { addToPractices } = useCombinedItemBulkAction();

    const {
        isLoadingItems,
        items = [],
        selectionModel,
        pagingModel,
        updateSelectionModel,
        columnsState,
        detailsPanelState,
        currentSearchQuery,
        updateCurrentSearchQuery,
    } = combinedItemStore;
    const { isLoading: isConstantsLoading, organizationSettings } = constantsStore;
    const itemsGroupBy = organizationSettings?.itemsGroupBy;
    const isLoading = (!items.length && isLoadingItems) || isConstantsLoading;
    const grouping: CombinedItemsGridColumn =
        (itemsGroupBy === ItemsGroupByType.Description && CombinedItemsGridColumn.Description) || CombinedItemsGridColumn.Id;

    const searchItems = async (query: GetCombinedItemQuery): Promise<void> => {
        await combinedItemStore.fetchItems({ ...query });
    };

    const searchItemsForAutoComplete = async (query: GetCombinedItemQuery): Promise<CombinedItem[]> => {
        return await combinedItemStore.fetchItemsForAutoComplete(query);
    };

    const updateFilter = (searchValue: GetCombinedItemQuery) => {
        updateCurrentSearchQuery(searchValue);
    };

    const onBulkActionChange = (props: { type: CombinedItemBulkUpdateType }): void => {
        const { type } = props;

        if (type === CombinedItemBulkUpdateType.AddToPractices) {
            addToPractices.action();
        }

        // if (
        //     type === CombinedItemBulkUpdateType.Description ||
        //     type === CombinedItemBulkUpdateType.HideZeroAmounts
        // ) {
        //     bulkUpdate.action(type);
        // }
    };

    return (
        <div className={styles.holder} data-automation-id="combined-items-search-results">
            <Container className={styles.container} fluid={true}>
                <CombinedItemSearchPanel
                    value={currentSearchQuery}
                    onChange={updateFilter}
                    searchItems={searchItems}
                    searchItemsForAutoComplete={searchItemsForAutoComplete}
                />

                {isLoading && <SpotLoading />}
                {Boolean(items?.length) && (
                    <CombinedItemsGrid
                        className={styles.grid}
                        items={items}
                        loading={isLoadingItems}
                        grouping={[grouping]}
                        selection={selectionModel}
                        pagination={pagingModel}
                        onSelectionChange={updateSelectionModel}
                        onBulkActionChange={onBulkActionChange}
                        columnsState={columnsState}
                        onColumnsStateChange={(state) => {
                            combinedItemStore.updateColumnsState(state);
                        }}
                        detailsPanelState={detailsPanelState}
                        onDetailsPanelStateChange={(state) => {
                            combinedItemStore.updateDetailsPanelState(state);
                        }}
                    />
                )}
            </Container>

            {addToPractices.itemsKeys && (
                <ItemAddPractices
                    sitesWithoutItems={addToPractices.practicesWithoutItems}
                    title={i18n.t('controlCenter:addThisCombinedItemToAdditionalPractices', 'Add this Combi Product to Additional Practices', {
                        count: addToPractices.itemsKeys.size,
                    })}
                    secondaryTitle={i18n.t('controlCenter:itemsSelected', `[${addToPractices.itemsKeys.size}] Item(s) Selected`, {
                        count: addToPractices.itemsKeys.size,
                    })}
                    itemTtitle={i18n.t('controlCenter:combiProduct', `Combi Product`)}
                    onSubmit={async (params) => {
                        const newItems = await addToPractices.submit(params);

                        if (newItems?.length) {
                            updateSelectionModel(undefined);
                        }
                    }}
                    onClose={addToPractices.cancel}
                />
            )}
        </div>
    );
});
